import { $config } from '@client/core/atoms/config.js';
import pulse from '@finn-no/pulse-sdk';
import type { TrackerData } from '@finn-no/pulse-sdk/dist/types.js';
import { debugLog } from '@schibsted-nmp/advertising-shared';

import type { RetailMediaProduct } from '../types/index.js';

type WithoutNullish<T> = { [K in keyof T]?: NonNullable<T[K]> };

const removeNullish = <T extends Record<string, unknown>>(
  obj: T
): WithoutNullish<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value != null)
  ) as WithoutNullish<T>;
};

type RetailMediaTrackingContext = {
  zone: string;
  categories?: Array<string> | undefined;
  placementId?: string;
};

export function trackImpressionPulse(
  product: RetailMediaProduct,
  context: RetailMediaTrackingContext
) {
  const config = $config.get();

  const vertical: TrackerData['vertical'] = config?.vertical
    ? {
        name: String(config.vertical),
        ...(config.subvertical && { subVertical: String(config.subvertical) })
      }
    : undefined;

  const tags = [context.zone, ...(context.categories ?? [])].filter(Boolean);

  const pulseEvent = removeNullish({
    type: 'View',
    intent: 'Show',
    name: 'Sponsored Product Ad',
    object: {
      type: 'UIElement',
      id: product.adId,
      name: product.title,
      adPosition: context.placementId,
      tags: tags.length > 0 ? tags : undefined
    },
    vertical,
    consent: config?.consent ?? undefined
  });

  try {
    pulse.trackEvent(pulseEvent as TrackerData);
    debugLog('[pulse] tracked retail media impression', pulseEvent);
  } catch (error) {
    debugLog('[pulse] failed to track retail media impression', error);
  }
}

export function trackClickPulse(
  product: RetailMediaProduct,
  context: RetailMediaTrackingContext
) {
  const config = $config.get();

  const vertical: TrackerData['vertical'] = config?.vertical
    ? {
        name: String(config.vertical),
        ...(config.subvertical && { subVertical: String(config.subvertical) })
      }
    : undefined;

  const tags = [context.zone, ...(context.categories ?? [])].filter(Boolean);

  const pulseEvent = removeNullish({
    type: 'Click',
    intent: 'Send',
    name: 'Sponsored Product Ad',
    object: {
      type: 'ExternalContent',
      id: product.adId,
      name: product.title,
      adPosition: context.placementId,
      tags: tags.length > 0 ? tags : undefined
    },
    target: {
      type: 'ExternalContent',
      id: `${product.brand} - ${product.title}`,
      url: product.productUrl,
      provider: product.advertiser
    },
    vertical,
    consent: config?.consent ?? undefined
  });

  try {
    pulse.trackEvent(pulseEvent as TrackerData);
    debugLog('[pulse] tracked retail media click', pulseEvent);
  } catch (error) {
    debugLog('[pulse] failed to track retail media click', error);
  }
}
